import cn from 'classnames';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import React, { memo } from 'react';
import { StickyContainer } from 'react-sticky';

import { useIsMobile } from '@sravni/react-utils';

import type { CommonCardProps } from '@src/@types/commonCardProps';
import { usePageContext } from '@src/config/PageContext';
import { PRODUCT_LIST_ANCHOR } from '@src/constants/common';
import { DEFAULT_LIMIT, FILTERS_NAMES } from '@src/constants/filters';
import type { IFilter } from '@src/hooks/useFilters';
import { useSelectCreditsList } from '@src/reducers/microcredits';
import { isSpecialRoutesPage } from '@src/utils/routing';
import { ProductListCommon } from 'entities/Microcredits/ui/ProductListCommon/ProductListCommon';
import { ShowMoreButton } from 'features/ProductList/ui/ShowMoreButton';
import { useCrmVitrinaVisited } from 'shared/lib/analytics/crmVitrina';

import { SortPanel } from '../SortPanel';

import styles from './styles.module.scss';

interface IProps {
  className?: string;
  listClassName?: string;
  gridLayout?: boolean;

  pathname?: string;
  filter: IFilter;
  showMoreButton?: ReactNode;

  renderCard?: (props: CommonCardProps) => ReactNode;
}

// eslint-disable-next-line complexity
const ProductList: React.FC<IProps> = memo(
  ({ className, listClassName, gridLayout, filter, showMoreButton, renderCard }) => {
    const { listKey, sortPanelVisible } = usePageContext();
    const { items, total = 0, loadingMore } = useSelectCreditsList(listKey);
    const { asPath } = useRouter();
    const filterValue = filter.filters[FILTERS_NAMES.OFFERS_TYPE];
    const offerType = filterValue || 'new';
    const isMobile = useIsMobile();
    const isSpecial = isSpecialRoutesPage(asPath);

    const limit = filter.filters[FILTERS_NAMES.LIMIT] || DEFAULT_LIMIT;
    const activeElements = items?.length || 0;
    const loadItemCount = total - activeElements;
    const preloadTotal = Math.min(loadItemCount, limit);
    const isRenderShowMoreButton: boolean =
      (preloadTotal && activeElements) > 0 && [total, !loadingMore, total !== activeElements].some(Boolean);

    const isSortPanelShowed = sortPanelVisible && Boolean(items?.length) && offerType === 'new' && !isMobile;

    useCrmVitrinaVisited(Boolean(items?.length));

    return (
      <StickyContainer id={PRODUCT_LIST_ANCHOR} className={cn(styles.sticky_container, className)}>
        {isSortPanelShowed && (
          <SortPanel sort={filter.filters[FILTERS_NAMES.SORT]} onChange={filter.setFilter} hasSortLabel />
        )}

        <ProductListCommon
          preloadTotal={preloadTotal}
          filter={filter}
          renderCard={renderCard}
          className={listClassName}
          gridLayout={gridLayout}
        />

        {isRenderShowMoreButton &&
          preloadTotal > 0 &&
          !isSpecial &&
          (showMoreButton || (
            <ShowMoreButton listKey={listKey} preloadTotal={preloadTotal} loadItemCount={loadItemCount} />
          ))}
      </StickyContainer>
    );
  },
);

export default ProductList;
